import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ShieldCheck, Lock, FileCheck2, Eye, MessageSquare, Timer, BarChart3, KeyRound, HeartHandshake, Scale, Users,
  UserX, ClipboardList, CheckCircle2, ArrowRight, Sparkles, Building2
} from "lucide-react";

const features = [
  { icon: UserX, title: "Denúncia 100% anônima", body: "Sem IP, sem cookies, sem metadados identificáveis. Anonimização técnica real." },
  { icon: MessageSquare, title: "Chat anônimo bidirecional", body: "Denunciante e compliance dialogam preservando o sigilo total da identidade." },
  { icon: KeyRound, title: "Protocolo + senha própria", body: "O denunciante define sua senha de acompanhamento e recebe protocolo único." },
  { icon: Lock, title: "Criptografia AES-256 + TLS", body: "Dados em trânsito e em repouso protegidos, alinhados à ISO/IEC 27001." },
  { icon: BarChart3, title: "Relatórios para o PGR", body: "Exportação de indicadores para auditoria trabalhista e alimentação do PGR NR-1." },
  { icon: Timer, title: "SLA e triagem inteligente", body: "Fila de casos, atribuição, prazos e trilha imutável de auditoria." },
];

const steps = [
  { n: "01", title: "Denúncia registrada", body: "Colaborador ou terceiro preenche o formulário público no site da empresa." },
  { n: "02", title: "Protocolo gerado", body: "Sistema emite número único e senha de acompanhamento definida pelo denunciante." },
  { n: "03", title: "Investigação sigilosa", body: "Equipe de compliance triaga, investiga e dialoga pelo chat anônimo." },
  { n: "04", title: "Encerramento auditável", body: "Classificação final, ações corretivas e evidência documental para o PGR." },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(79,70,229,0.25),transparent_50%),radial-gradient(circle_at_80%_20%,rgba(13,148,136,0.18),transparent_45%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/15 border border-teal-500/30 text-teal-300 text-xs font-semibold uppercase tracking-widest mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Conforme NR-1 · ISO 45003 · Leis 14.831/2024 e 14.457/2022 · ISO 27001 · LGPD
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-white">
              Canal de denúncia<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-teal-400">100% anônimo</span> para sua empresa
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              Plataforma pronta para atender à Portaria MTE 1.419/2024 (NR-1) e aos controles ISO 27001. Sigilo absoluto, protocolos únicos e relatórios prontos para o PGR — em minutos.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => nav("/registro")} data-testid="hero-cta-register" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Ativar canal por R$ 299/mês <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => nav("/denunciar/acme")} data-testid="hero-cta-demo" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800">
                Ver formulário de exemplo
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-slate-400">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal-400" /> Trilha de auditoria imutável</div>
              <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-teal-400" /> Criptografia AES-256</div>
              <div className="flex items-center gap-2"><UserX className="w-4 h-4 text-teal-400" /> Zero rastreamento</div>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-indigo-500/20 to-teal-500/20 rounded-3xl blur-2xl" />
              <Card className="relative bg-slate-900/80 border-slate-800 p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-teal-500 grid place-items-center">
                      <ShieldCheck className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">Canal de denúncia</div>
                      <div className="text-xs text-slate-400">Empresa protegida por vixvox</div>
                    </div>
                  </div>
                  <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/40">Anônimo</Badge>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
                    <div className="text-xs text-slate-400 uppercase tracking-widest mb-1">Categoria</div>
                    <div className="text-white font-medium">Assédio moral · Setor Produção</div>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
                    <div className="text-xs text-slate-400 uppercase tracking-widest mb-1">Protocolo emitido</div>
                    <div className="font-mono text-teal-300 text-lg">DEN-2026-000123</div>
                  </div>
                  <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-indigo-300 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-200">Denúncia registrada com sigilo. A equipe de compliance foi notificada e o SLA de triagem está ativo.</div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-400 mb-3">Recursos</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Tudo que a NR-1 exige, entregue com a segurança da ISO 27001</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <Card key={i} data-testid={`feature-card-${i}`} className="p-6 bg-slate-900/50 border-slate-800 hover:border-teal-500/50 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500/20 to-teal-500/20 border border-slate-700 grid place-items-center mb-4">
                  <f.icon className="w-5 h-5 text-teal-300" />
                </div>
                <h3 className="font-display text-lg font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section id="compliance" className="py-20 border-t border-slate-800 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-400 mb-3">Conformidade</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white mb-6">Do canal de escuta ao PGR — sem retrabalho</h2>
            <p className="text-slate-300 leading-relaxed mb-8">
              Desde agosto de 2024, a NR-1 exige que empresas mantenham canal formal para riscos psicossociais e alimentem o PGR com evidências. O vixvox entrega tudo pronto: categorias alinhadas, vinculação de GHE, relatórios auditáveis e trilha imutável.
            </p>
            <p className="text-slate-300 leading-relaxed mb-8" data-testid="compliance-alignment-text">
              A plataforma está alinhada à <span className="text-white font-medium">NR-1</span>, à <span className="text-white font-medium">ISO 45003:2022</span> (saúde psicológica e segurança no trabalho) e às <span className="text-white font-medium">Leis 14.831/2024</span> (Empresas Psicologicamente Seguras) e <span className="text-white font-medium">14.457/2022</span> (CIPAA — prevenção e combate ao assédio).
            </p>
            <ul className="space-y-3 text-slate-200 text-sm">
              {[
                "Categorias pré-configuradas para riscos psicossociais (NR-1 §1.5)",
                "Exportação PDF/JSON para fiscalização MTE",
                "Vinculação de denúncia a Setor / GHE (Grupo Homogêneo de Exposição)",
                "Mapeamento de controles ISO 27001: A.5, A.8, A.9, A.12, A.18",
                "Retenção configurável alinhada à LGPD",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: FileCheck2, title: "NR-1", sub: "MTE 1.419/2024" },
              { icon: HeartHandshake, title: "ISO 45003:2022", sub: "Saúde psicológica no trabalho" },
              { icon: Scale, title: "Lei 14.831/2024", sub: "Empresas Psicologicamente Seguras" },
              { icon: Users, title: "Lei 14.457/2022", sub: "CIPAA" },
              { icon: Lock, title: "ISO 27001", sub: "SGSI aplicado" },
              { icon: ShieldCheck, title: "LGPD", sub: "Lei 13.709/2018" },
              { icon: Eye, title: "Auditoria", sub: "Logs imutáveis" },
              { icon: KeyRound, title: "Sigilo", sub: "Anonimato garantido" },
            ].map((b, i) => (
              <Card key={i} className="p-6 bg-slate-900/60 border-slate-800 text-center">
                <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/30 grid place-items-center mx-auto mb-3">
                  <b.icon className="w-6 h-6 text-teal-300" />
                </div>
                <div className="font-display font-bold text-white">{b.title}</div>
                <div className="text-xs text-slate-400 mt-1">{b.sub}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-400 mb-3">Como funciona</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Um fluxo transparente do início ao encerramento</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="relative p-6 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="font-mono text-teal-400 text-sm mb-3">{s.n}</div>
                <h3 className="font-display text-lg font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.body}</p>
                {i < steps.length - 1 && <div className="hidden lg:block absolute top-1/2 -right-3 w-6 border-t border-dashed border-slate-700" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 border-t border-slate-800 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-400 mb-3">Plano único</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Tudo incluído, sem surpresas</h2>
            <p className="mt-4 text-slate-400">Ative seu canal em minutos. 7 dias de teste grátis.</p>
          </div>
          <Card className="p-8 lg:p-12 bg-gradient-to-br from-slate-900 to-slate-950 border-slate-800 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-teal-500/15 rounded-full blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-teal-300 font-mono text-xs uppercase tracking-widest mb-2">Plano Essencial</div>
                <div className="flex items-end gap-2 mb-4">
                  <span className="font-display text-6xl font-extrabold text-white">R$ 299</span>
                  <span className="text-slate-400 pb-2">/mês</span>
                </div>
                <p className="text-slate-300 mb-6">Canal completo, ilimitado em denúncias, com todos os módulos de conformidade.</p>
                <Button size="lg" onClick={() => nav("/registro")} data-testid="pricing-cta" className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto">
                  Iniciar teste de 7 dias <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <ul className="space-y-3 text-slate-200 text-sm">
                {[
                  "Denúncias ilimitadas, anônimas e identificadas",
                  "Chat bidirecional anônimo",
                  "Dashboard de gestão com KPIs e SLA",
                  "Relatórios PDF/Excel para PGR e auditoria",
                  "Trilha de auditoria imutável (ISO 27001)",
                  "URL exclusiva e QR Code para cartazes",
                  "Suporte por e-mail em pt-BR",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-400 mb-3">Dúvidas frequentes</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Perguntas do RH, compliance e diretoria</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {[
              { q: "A denúncia anônima é realmente anônima?", a: "Sim. Ao selecionar 'anônima' o sistema não coleta IP, e-mail, cookies ou qualquer metadado identificável. O denunciante define sua senha de acompanhamento e o protocolo é a única chave de acesso." },
              { q: "Como o vixvox atende à NR-1?", a: "As categorias de denúncia estão alinhadas aos fatores de risco psicossocial da Portaria MTE 1.419/2024, e todos os relatórios podem ser exportados para compor o PGR como evidência do canal de escuta." },
              { q: "Quais controles ISO 27001 estão implementados?", a: "Criptografia AES-256 em repouso e TLS 1.2+ em trânsito, RBAC, MFA para administradores, trilha de auditoria imutável, política de retenção e descarte configurável, backups criptografados e segregação lógica multi-tenant." },
              { q: "Preciso ter conta em algum serviço externo?", a: "Não. O vixvox roda 100% em nuvem. Você recebe uma URL exclusiva e um QR Code para divulgar em murais e comunicados internos." },
              { q: "Posso cancelar a qualquer momento?", a: "Sim. A assinatura é mensal, sem multa. Ao encerrar, os dados são retidos pelo período legal e depois descartados conforme sua política LGPD." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-slate-900/50 border border-slate-800 rounded-lg px-4">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-slate-100 hover:text-white text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <Building2 className="w-12 h-12 text-teal-400 mx-auto mb-4" />
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Sua empresa em conformidade em 7 minutos</h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">Cadastre sua organização, receba a URL do canal e comece a operar hoje mesmo. Nada de contratos longos ou implantação demorada.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={() => nav("/registro")} data-testid="cta-final-register" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              Ativar meu canal <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Link to="/acompanhar">
              <Button size="lg" variant="outline" data-testid="cta-final-followup" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800">
                Sou denunciante — acompanhar caso
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
